import { CLINIC_NAME } from '../constants';

const STEPS = [
  { n:'01', title:'Book an Appointment', desc:'Call us or walk in. Our front-desk team will match you with the right specialist and preferred time slot.' },
  { n:'02', title:'Meet Your Doctor', desc:'Visit your fully-equipped consultation room. Experienced practitioners provide thorough, unhurried care.' },
  { n:'03', title:'Get Diagnosed', desc:'Use our on-site lab for blood work and diagnostics — no extra trips, same visit results.' },
  { n:'04', title:'Collect Your Prescription', desc:'Our in-house pharmacy fills prescriptions immediately. Walk out fully cared for, same visit.' },
];

const AMENITIES = [
  { icon:'💊', title:'On-Site Pharmacy', desc:'Fill prescriptions immediately — no extra trips or waiting.' },
  { icon:'🔬', title:'Same-Visit Diagnostics', desc:'In-house lab so results come back fast, often same visit.' },
  { icon:'👩‍⚕️', title:'Nursing Support', desc:'Attentive nursing staff to assist throughout your visit.' },
  { icon:'🏢', title:'Modern Facility', desc:'AC, lift and premium interiors for a comfortable experience.' },
  { icon:'🅿️', title:'Parking Available', desc:'Ample, accessible parking right at the facility.' },
  { icon:'🩺', title:'All Specialties', desc:'See multiple specialists in a single visit under one roof.' },
];

const FAQS = [
  { q:'Do I need an appointment?', a:'Walk-ins are welcome, but booking in advance ensures minimal wait time and your preferred doctor.' },
  { q:'What are your operating hours?', a:'Monday–Saturday: 9 AM – 8 PM. Sunday: 10 AM – 2 PM.' },
  { q:'Is there parking available?', a:'Yes, ample parking is available at our facility.' },
  { q:'Can I consult multiple specialists in one visit?', a:'Absolutely. With all specialties under one roof, you can see multiple doctors in a single visit.' },
  { q:`When is ${CLINIC_NAME} opening?`, a:'We are launching in 2026. Register your interest today and we will keep you updated.' },
];

export default function Patients({ onNav }) {
  const go = (id) => { onNav(id); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  return (
    <>
      <style>{`
        .pat-hero {
          padding: 120px 0 64px;
          background: linear-gradient(160deg, var(--charcoal) 0%, #0a1730 100%);
          position: relative; overflow: hidden;
        }
        .pat-hero::before {
          content:''; position:absolute; inset:0;
          background: radial-gradient(circle at 20% 70%, rgba(30,58,95,0.1) 0%, transparent 55%);
        }
        .pat-hero-inner { position: relative; }

        .steps-section { background: var(--sand); }
        .steps-grid { display: grid; grid-template-columns: repeat(2,1fr); gap: 18px; margin-top: 36px; }
        .step-card {
          background: var(--white); border: 1px solid var(--border);
          border-radius: var(--radius); padding: 26px; display: flex; gap: 16px;
        }
        .step-n {
          font-family: var(--font-display); font-size: 36px; font-weight: 600;
          color: var(--terra); opacity: 0.25; line-height: 1; flex-shrink: 0; min-width: 44px;
        }
        .step-title { font-size: 14.5px; font-weight: 600; color: var(--charcoal); margin-bottom: 6px; }
        .step-desc { font-size: 13px; color: var(--text-muted); line-height: 1.65; }

        .amenities-section { background: var(--white); }
        .am-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 18px; margin-top: 36px; }
        .am-card {
          background: var(--sand); border: 1px solid var(--border);
          border-radius: var(--radius); padding: 24px; text-align: center; transition: all 0.2s;
        }
        .am-card:hover { border-color: rgba(30,58,95,0.2); transform: translateY(-2px); }
        .am-icon { font-size: 28px; margin-bottom: 10px; }
        .am-title { font-size: 14px; font-weight: 600; color: var(--charcoal); margin-bottom: 5px; }
        .am-desc { font-size: 12.5px; color: var(--text-muted); line-height: 1.6; }

        .facility-img-section { background: var(--sand-mid); }
        .facility-img-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin-top: 36px; }
        .fac-img-card { border-radius: var(--radius); overflow: hidden; box-shadow: var(--shadow); }
        .fac-img-card img { width: 100%; height: 220px; object-fit: cover; display: block; }
        .fac-img-label {
          padding: 12px 16px; background: var(--white); border-top: 1px solid var(--border);
          font-size: 13px; font-weight: 500; color: var(--text-mid);
        }

        .faq-section { background: var(--white); }
        .faq-list { margin-top: 32px; }
        .faq-item { border-bottom: 1px solid var(--border); padding: 18px 0; }
        .faq-item:first-child { border-top: 1px solid var(--border); }
        .faq-q { font-size: 15px; font-weight: 500; color: var(--charcoal); margin-bottom: 7px; }
        .faq-a { font-size: 13.5px; color: var(--text-muted); line-height: 1.7; }

        @media (max-width: 768px) {
          .steps-grid { grid-template-columns: 1fr; }
          .am-grid { grid-template-columns: repeat(2,1fr); }
          .facility-img-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 480px) {
          .am-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="pat-hero">
        <div className="container pat-hero-inner">
          <span className="eyebrow" style={{color:'var(--accent)'}}>For Patients</span>
          <h1 style={{fontFamily:'var(--font-display)',fontSize:'clamp(34px,5vw,54px)',fontWeight:600,color:'white',lineHeight:1.1,letterSpacing:'-0.01em',marginBottom:14,maxWidth:520}}>
            You deserve care<br /><em style={{fontStyle:'italic',color:'var(--accent)'}}>that puts you first.</em>
          </h1>
          <p style={{fontSize:15,color:'rgba(255,255,255,0.55)',maxWidth:480,lineHeight:1.75,marginBottom:28}}>
            At {CLINIC_NAME}, every visit is designed to be smooth, dignified and thorough — right here in Siddipet.
          </p>
          <button className="btn-accent" onClick={() => go('contact')}>📅 Book Appointment</button>
        </div>
      </div>

      <section className="section steps-section">
        <div className="container">
          <span className="eyebrow">How It Works</span>
          <h2 className="section-title">Your visit, step by step</h2>
          <div className="steps-grid">
            {STEPS.map(s => (
              <div key={s.n} className="step-card">
                <div className="step-n">{s.n}</div>
                <div>
                  <div className="step-title">{s.title}</div>
                  <div className="step-desc">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section amenities-section">
        <div className="container">
          <span className="eyebrow">Patient Amenities</span>
          <h2 className="section-title">Comfort at every step</h2>
          <div className="am-grid">
            {AMENITIES.map(a => (
              <div key={a.title} className="am-card">
                <div className="am-icon">{a.icon}</div>
                <div className="am-title">{a.title}</div>
                <div className="am-desc">{a.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section facility-img-section">
        <div className="container">
          <span className="eyebrow">Our Facility</span>
          <h2 className="section-title">Built for your comfort</h2>
          <div className="facility-img-grid">
            <div className="fac-img-card">
              <img src="/images/pharmacy.jpg" alt="On-site pharmacy" />
              <div className="fac-img-label">💊 On-Site Licensed Pharmacy</div>
            </div>
            <div className="fac-img-card">
              <img src="/images/diagnostics.jpg" alt="Diagnostics" />
              <div className="fac-img-label">🔬 In-House Diagnostics Lab</div>
            </div>
          </div>
        </div>
      </section>

      <section className="section faq-section">
        <div className="container">
          <span className="eyebrow">FAQs</span>
          <h2 className="section-title">Common questions</h2>
          <div className="faq-list">
            {FAQS.map(f => (
              <div key={f.q} className="faq-item">
                <div className="faq-q">{f.q}</div>
                <div className="faq-a">{f.a}</div>
              </div>
            ))}
          </div>
          <div style={{marginTop:36}}>
            <button className="btn-terra" onClick={() => go('contact')}>Book an Appointment →</button>
          </div>
        </div>
      </section>
    </>
  );
}
